import React, {useEffect, useRef, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {Page, PageHeader, Panel} from "../../../../components/kit";
import {useApiManager} from "../../../../components/utils/api";
import {BankAccountList, BankAccountRestControllerApiFactory, ContasolRestControllerApiFactory} from "arteco-api-client-ts";
import {fmtAmt} from "../../../../components/utils/numbers";

interface ImportResult {
    totalEntries: number;
    created: number;
    skipped: number;
    reimported: number;
    cleared: number;
    errors: string[];
    newAccountsCreated: string[];
    bankAccountsCreated: string[];
    suppliersCreated: string[];
    fixedAssetsCreated: string[];
}

interface EntityResult {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
}

interface ReconcileResult {
    billsMatched: number;
    autoFacturaMatched: number;
    errors: string[];
}

interface DivergenceRow {
    entryId: number;
    entryDate: string;
    description: string;
    amount: number;
    possibleContasolDate: string | null;
}

interface ValidationRow {
    accountCode: string;
    accountName: string;
    contasolBalance: number;
    artecoBalance: number;
    difference: number;
    match: boolean;
}

export const ContasolImporterPage = () => {
    const apiManager = useApiManager();
    const contCtrl = apiManager.factory(ContasolRestControllerApiFactory);

    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [importError, setImportError] = useState<string | null>(null);

    const [validating, setValidating] = useState(false);
    const [validationRows, setValidationRows] = useState<ValidationRow[] | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    const [supplierResult, setSupplierResult] = useState<EntityResult | null>(null);
    const [customerResult, setCustomerResult] = useState<EntityResult | null>(null);
    const [invoiceResult, setInvoiceResult] = useState<EntityResult | null>(null);
    const [bankMovResult, setBankMovResult] = useState<EntityResult | null>(null);
    const [bankAccounts, setBankAccounts] = useState<BankAccountList[]>([]);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<number | "">("");

    useEffect(() => {
        const ctrl = apiManager.factory(BankAccountRestControllerApiFactory);
        apiManager.execute(ctrl.listBankAccount()).then(list => setBankAccounts(list));
    }, []);

    const multipartOpts = {headers: {'Content-Type': null as any}};

    const toFormData = (file: File) => {
        const fd = new FormData();
        fd.append('file', file);
        return fd;
    };

    const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const file = e.target.files[0];
        setImporting(true);
        setImportResult(null);
        setImportError(null);
        apiManager.execute(contCtrl.importJournal(year, toFormData(file) as any, multipartOpts))
            .then(data => setImportResult(data as unknown as ImportResult))
            .catch(err => setImportError(err?.response?.data?.message ?? err?.message))
            .finally(() => {
                setImporting(false);
                e.target.value = "";
            });
    };

    const handleValidate = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const file = e.target.files[0];
        setValidating(true);
        setValidationRows(null);
        setValidationError(null);
        apiManager.execute(contCtrl.validateBalance(year, toFormData(file) as any, multipartOpts))
            .then(data => setValidationRows(data as unknown as ValidationRow[]))
            .catch(err => setValidationError(err?.response?.data?.message ?? err?.message))
            .finally(() => {
                setValidating(false);
                e.target.value = "";
            });
    };

    const handleSuppliers = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const file = e.target.files[0];
        setSupplierError(null);
        apiManager.execute(contCtrl.importSuppliers(toFormData(file) as any, multipartOpts))
            .then(data => setSupplierResult(data as unknown as EntityResult))
            .catch(err => setSupplierError(err?.response?.data?.message ?? err?.message))
            .finally(() => { e.target.value = ""; });
    };

    const handleCustomers = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const file = e.target.files[0];
        setCustomerError(null);
        apiManager.execute(contCtrl.importCustomers(toFormData(file) as any, multipartOpts))
            .then(data => setCustomerResult(data as unknown as EntityResult))
            .catch(err => setCustomerError(err?.response?.data?.message ?? err?.message))
            .finally(() => { e.target.value = ""; });
    };

    const handleInvoices = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const file = e.target.files[0];
        setInvoiceError(null);
        apiManager.execute(contCtrl.importInvoices(toFormData(file) as any, multipartOpts))
            .then(data => setInvoiceResult(data as unknown as EntityResult))
            .catch(err => setInvoiceError(err?.response?.data?.message ?? err?.message))
            .finally(() => { e.target.value = ""; });
    };

    const forceFileInputRef = useRef<HTMLInputElement>(null);
    const [forceImporting, setForceImporting] = useState(false);
    const [forceImportResult, setForceImportResult] = useState<ImportResult | null>(null);
    const [forceImportError, setForceImportError] = useState<string | null>(null);
    const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
    const [pendingForceFile, setPendingForceFile] = useState<File | null>(null);

    const handleForceImportSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        setPendingForceFile(e.target.files[0]);
        setForceConfirmOpen(true);
        e.target.value = "";
    };

    const handleForceImportConfirm = () => {
        if (!pendingForceFile) return;
        setForceConfirmOpen(false);
        setForceImporting(true);
        setForceImportResult(null);
        setForceImportError(null);
        const fd = new FormData();
        fd.append("file", pendingForceFile);
        apiManager.execute(contCtrl.forceReimportJournal(year, fd as any, multipartOpts))
            .then(data => setForceImportResult(data as unknown as ImportResult))
            .catch(err => setForceImportError(err?.response?.data?.message ?? err?.message))
            .finally(() => { setForceImporting(false); setPendingForceFile(null); });
    };

    const [bankMovError, setBankMovError] = useState<string | null>(null);
    const [supplierError, setSupplierError] = useState<string | null>(null);
    const [customerError, setCustomerError] = useState<string | null>(null);
    const [invoiceError, setInvoiceError] = useState<string | null>(null);
    const [matchResult, setMatchResult] = useState<EntityResult | null>(null);
    const [matchError, setMatchError] = useState<string | null>(null);
    const [matchBillsResult, setMatchBillsResult] = useState<EntityResult | null>(null);
    const [matchBillsError, setMatchBillsError] = useState<string | null>(null);
    const [reconcileResult, setReconcileResult] = useState<ReconcileResult | null>(null);
    const [reconcileError, setReconcileError] = useState<string | null>(null);
    const [divergences, setDivergences] = useState<DivergenceRow[] | null>(null);
    const [divergenceError, setDivergenceError] = useState<string | null>(null);

    const handleBankMovements = () => {
        if (!selectedBankAccountId) {
            setBankMovError("Selecciona una cuenta bancaria antes de generar los movimientos.");
            return;
        }
        setBankMovError(null);
        setBankMovResult(null);
        apiManager.execute(contCtrl.generateBankMovements(year, selectedBankAccountId as number))
            .then(data => setBankMovResult(data as unknown as EntityResult))
            .catch(err => setBankMovError(err?.response?.data?.message ?? err?.message));
    };

    const mismatches = validationRows?.filter(r => !r.match) ?? [];

    return (
        <Page>
            <PageHeader title="Importar Contasol"
                        subtitle="Los pasos van en orden: diario, validación del balance, movimientos bancarios, terceros, facturas y conciliación."/>
            <Stack spacing={3}>

                <Panel>
                <Grid container spacing={2} alignItems="center">
                    <Grid item>
                        <TextField
                            label="Año"
                            type="number"
                            value={year}
                            onChange={e => setYear(Number(e.target.value))}
                            inputProps={{min: 2012, max: new Date().getFullYear()}}
                            size="small"
                            sx={{width: 100}}
                        />
                    </Grid>
                    <Grid item>
                        <Typography variant="body2" color="text.secondary">
                            Ejercicio contable a importar / validar
                        </Typography>
                    </Grid>
                </Grid>
                </Panel>

                {/* ── Import section ── */}
                <Panel title="1. Importar el libro diario">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Sube el fichero XLS exportado desde Contasol: <em>Diario de movimientos oficial</em>.
                    Selecciona el año correcto antes de importar. Si el período no existe en Arteco se crea
                    automáticamente (CERRADO para años pasados, ABIERTO para el año actual).
                    La importación es idempotente: los asientos ya importados se omiten al reimportar.
                </Typography>
                <Button component="label" variant="contained" disabled={importing}>
                    {importing ? "Importando…" : "Seleccionar XLS e importar"}
                    <input type="file" accept=".xls,.XLS" hidden onChange={handleImport}/>
                </Button>

                {importError && (
                    <Alert severity="error" sx={{mt: 2}}>{importError}</Alert>
                )}
                {importResult && (
                    <Box sx={{mt: 2}}>
                        <Alert
                            severity={importResult.errors.length > 0 ? "warning" : "success"}
                            sx={{mb: 1}}
                        >
                            Asientos totales: <strong>{importResult.totalEntries}</strong> —
                            Creados: <strong>{importResult.created}</strong> —
                            Omitidos (ya existían): <strong>{importResult.skipped}</strong>
                            {importResult.reimported > 0 && <> — Reimportados (inválidos corregidos): <strong>{importResult.reimported}</strong></>}
                            {importResult.errors.length > 0 && ` — Errores: ${importResult.errors.length}`}
                        </Alert>
                        {importResult.bankAccountsCreated.length > 0 && (
                            <Box sx={{mb: 1}}>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                    Cuentas bancarias creadas ({importResult.bankAccountsCreated.length}) — completa el IBAN en Cuentas Bancarias:
                                </Typography>
                                {importResult.bankAccountsCreated.map((a, i) => (
                                    <Typography key={i} variant="body2" sx={{pl: 1}}>• {a}</Typography>
                                ))}
                            </Box>
                        )}
                        {importResult.suppliersCreated.length > 0 && (
                            <Box sx={{mb: 1}}>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                    Proveedores creados ({importResult.suppliersCreated.length}) — completa NIF/email en Proveedores:
                                </Typography>
                                {importResult.suppliersCreated.map((a, i) => (
                                    <Typography key={i} variant="body2" sx={{pl: 1}}>• {a}</Typography>
                                ))}
                            </Box>
                        )}
                        {importResult.fixedAssetsCreated.length > 0 && (
                            <Box sx={{mb: 1}}>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                    Activos fijos creados ({importResult.fixedAssetsCreated.length}) — completa fecha/vida útil/método en Activos Fijos:
                                </Typography>
                                {importResult.fixedAssetsCreated.map((a, i) => (
                                    <Typography key={i} variant="body2" sx={{pl: 1}}>• {a}</Typography>
                                ))}
                            </Box>
                        )}
                        {importResult.newAccountsCreated.length > 0 && (
                            <Box sx={{mb: 1}}>
                                <Typography variant="body2" fontWeight="bold">
                                    Cuentas contables creadas automáticamente ({importResult.newAccountsCreated.length}):
                                </Typography>
                                {importResult.newAccountsCreated.map((a, i) => (
                                    <Typography key={i} variant="body2" sx={{fontFamily: "monospace", pl: 1}}>
                                        {a}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                        {importResult.errors.length > 0 && (
                            <Box>
                                <Typography variant="body2" fontWeight="bold" color="error">Errores:</Typography>
                                {importResult.errors.map((e, i) => (
                                    <Typography key={i} variant="body2" color="error" sx={{pl: 1}}>{e}</Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}


                </Panel>

                {/* ── Force reimport section ── */}
                <Panel title={<Box component="span" sx={{color: "error.main"}}>1b. Forzar la reimportación completa</Box>}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    <strong>¡Operación destructiva!</strong> Elimina todos los asientos ContaSol del año seleccionado
                    (y sus movimientos bancarios asociados) y los reimporta desde cero. Usar cuando ContaSol haya
                    renumerado asientos al añadir nuevas entradas intermedias. Los proveedores, clientes y activos
                    fijos creados previamente NO se eliminan.
                </Typography>
                <Button
                    variant="outlined"
                    color="error"
                    disabled={forceImporting}
                    onClick={() => forceFileInputRef.current?.click()}
                    startIcon={forceImporting ? <CircularProgress size={16} color="inherit"/> : undefined}
                >
                    {forceImporting ? "Reimportando…" : "Seleccionar XLS y forzar reimportación"}
                </Button>
                <input ref={forceFileInputRef} type="file" accept=".xls,.XLS" hidden onChange={handleForceImportSelect}/>
                {forceImportError && <Alert severity="error" sx={{mt: 1}}>{forceImportError}</Alert>}
                {forceImportResult && (
                    <Alert severity={forceImportResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Purgados: <strong>{forceImportResult.cleared}</strong> —
                        Creados: <strong>{forceImportResult.created}</strong> —
                        Errores: <strong>{forceImportResult.errors.length}</strong>
                        {forceImportResult.errors.map((e, i) => (
                            <Typography key={i} variant="body2" color="error" sx={{pl: 1}}>• {e}</Typography>
                        ))}
                    </Alert>
                )}

                {/* Confirmation dialog */}
                <Dialog open={forceConfirmOpen} onClose={() => { setForceConfirmOpen(false); setPendingForceFile(null); }} PaperProps={{sx: {borderRadius: 3}}}>
                    <DialogTitle sx={{fontWeight: 700}}>¿Confirmar reimportación forzada?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Se eliminarán <strong>todos los asientos ContaSol del año {year}</strong> y sus movimientos
                            bancarios asociados. Esta acción no se puede deshacer fácilmente.
                            ¿Continuar con el fichero <em>{pendingForceFile?.name}</em>?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setForceConfirmOpen(false); setPendingForceFile(null); }}>Cancelar</Button>
                        <Button onClick={handleForceImportConfirm} color="error" variant="contained">
                            Sí, reimportar desde cero
                        </Button>
                    </DialogActions>
                </Dialog>

                </Panel>

                {/* ── Validation section ── */}
                <Panel title="2. Validar el balance con Contasol">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Sube el fichero XLS <em>Balance de Sumas y Saldos</em> de Contasol para comparar saldos cuenta a cuenta.
                </Typography>
                <Button component="label" variant="outlined" disabled={validating}>
                    {validating ? "Validando…" : "Seleccionar XLS y validar"}
                    <input type="file" accept=".xls,.XLS" hidden onChange={handleValidate}/>
                </Button>

                {validationError && (
                    <Alert severity="error" sx={{mt: 2}}>{validationError}</Alert>
                )}
                {validationRows && (
                    <Box sx={{mt: 2}}>
                        <Alert severity={mismatches.length === 0 ? "success" : "warning"} sx={{mb: 2}}>
                            {mismatches.length === 0
                                ? `Todos los saldos cuadran (${validationRows.length} cuentas)`
                                : `${mismatches.length} cuenta(s) con diferencias de ${validationRows.length} totales`}
                        </Alert>
                        <Table size="small" sx={{tableLayout: "fixed"}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{width: 80}}>Cuenta</TableCell>
                                    <TableCell>Descripción</TableCell>
                                    <TableCell align="right" sx={{width: 120}}>Contasol</TableCell>
                                    <TableCell align="right" sx={{width: 120}}>Arteco</TableCell>
                                    <TableCell align="right" sx={{width: 120}}>Diferencia</TableCell>
                                    <TableCell align="center" sx={{width: 70}}>OK</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {validationRows.map((row, i) => (
                                    <TableRow
                                        key={i}
                                        sx={{backgroundColor: row.match ? undefined : "#fff3e0"}}
                                    >
                                        <TableCell sx={{fontFamily: "monospace"}}>{row.accountCode}</TableCell>
                                        <TableCell>{row.accountName}</TableCell>
                                        <TableCell align="right" sx={{fontFamily: "monospace"}}>
                                            {fmtAmt(row.contasolBalance)}
                                        </TableCell>
                                        <TableCell align="right" sx={{fontFamily: "monospace"}}>
                                            {fmtAmt(row.artecoBalance)}
                                        </TableCell>
                                        <TableCell align="right" sx={{fontFamily: "monospace"}}>
                                            {fmtAmt(row.difference)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={row.match ? "OK" : "NOK"}
                                                color={row.match ? "success" : "error"}
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                </Panel>

                {/* ── Bank movements section ── */}
                <Panel title="3. Generar los movimientos bancarios">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Genera automáticamente los movimientos bancarios (cobros y pagos) a partir de los
                    asientos del diario que mueven la cuenta 572. Requiere haber importado el diario primero.
                </Typography>
                <Box sx={{display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap"}}>
                    <FormControl size="small" sx={{minWidth: 260}}>
                        <InputLabel>Cuenta bancaria</InputLabel>
                        <Select
                            value={selectedBankAccountId}
                            label="Cuenta bancaria"
                            onChange={e => setSelectedBankAccountId(e.target.value as number | "")}
                        >
                            <MenuItem value=""><em>Selecciona una cuenta</em></MenuItem>
                            {bankAccounts.map(a => (
                                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={handleBankMovements}>
                        Generar movimientos bancarios para {year}
                    </Button>
                </Box>
                {bankMovError && <Alert severity="error" sx={{mt: 1}}>{bankMovError}</Alert>}
                {bankMovResult && (
                    <Alert severity={bankMovResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Creados: <strong>{bankMovResult.created}</strong> —
                        Omitidos (ya existían): <strong>{bankMovResult.skipped}</strong>
                        {bankMovResult.errors.length > 0 && ` — Errores: ${bankMovResult.errors.length}`}
                    </Alert>
                )}

                </Panel>

                {/* ── Suppliers section ── */}
                <Panel title="4. Importar proveedores">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Sube el fichero ODS <em>FICHERO DE PROVEEDORES</em>. Actualiza NIF, teléfono y dirección
                    en los proveedores ya existentes. No crea proveedores nuevos.
                </Typography>
                <Button component="label" variant="outlined">
                    Seleccionar ODS e importar proveedores
                    <input type="file" accept=".ods,.ODS" hidden onChange={handleSuppliers}/>
                </Button>
                {supplierError && <Alert severity="error" sx={{mt: 1}}>{supplierError}</Alert>}
                {supplierResult && (
                    <Alert severity={supplierResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Actualizados: <strong>{supplierResult.updated}</strong> —
                        Omitidos: <strong>{supplierResult.skipped}</strong>
                        {supplierResult.errors.length > 0 && ` — Errores: ${supplierResult.errors.length}`}
                    </Alert>
                )}

                </Panel>

                {/* ── Customers section ── */}
                <Panel title="5. Importar clientes">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Sube el fichero ODS <em>FICHERO DE CLIENTES</em>. Crea clientes nuevos o actualiza
                    NIF, teléfono y dirección en los existentes.
                </Typography>
                <Button component="label" variant="outlined">
                    Seleccionar ODS e importar clientes
                    <input type="file" accept=".ods,.ODS" hidden onChange={handleCustomers}/>
                </Button>
                {customerError && <Alert severity="error" sx={{mt: 1}}>{customerError}</Alert>}
                {customerResult && (
                    <Alert severity={customerResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Creados: <strong>{customerResult.created}</strong> —
                        Actualizados: <strong>{customerResult.updated}</strong> —
                        Omitidos: <strong>{customerResult.skipped}</strong>
                        {customerResult.errors.length > 0 && ` — Errores: ${customerResult.errors.length}`}
                    </Alert>
                )}

                </Panel>

                {/* ── Invoices section ── */}
                <Panel title="6. Importar facturas recibidas">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Sube el fichero XLS <em>FACTURAS RECIBIDAS</em> (Libro de IVA Soportado de Contasol).
                    Importa las facturas recibidas enlazándolas con el proveedor por NIF.
                    Importa primero los proveedores (sección 3) para que el NIF esté disponible.
                </Typography>
                <Button component="label" variant="outlined">
                    Seleccionar XLS e importar facturas
                    <input type="file" accept=".xls,.XLS" hidden onChange={handleInvoices}/>
                </Button>
                {invoiceError && <Alert severity="error" sx={{mt: 1}}>{invoiceError}</Alert>}
                {invoiceResult && (
                    <Alert severity={invoiceResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Creadas: <strong>{invoiceResult.created}</strong> —
                        Omitidas (ya existían): <strong>{invoiceResult.skipped}</strong>
                        {invoiceResult.errors.length > 0 && (
                            <Box sx={{mt: 0.5}}>
                                {invoiceResult.errors.map((e, i) => (
                                    <Typography key={i} variant="body2" color="error">• {e}</Typography>
                                ))}
                            </Box>
                        )}
                    </Alert>
                )}

                </Panel>

                {/* ── Match invoices with journal entries ── */}
                <Panel title="7. Vincular facturas recibidas con asientos">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Vincula automáticamente las facturas de proveedor importadas con los asientos
                    contables del mismo ejercicio. El criterio de match es: misma fecha de emisión,
                    misma cuenta contable de proveedor (40x) e importe total dentro de ±0,05 €.
                    Solo se vinculan matches únicos — si hay ambigüedad se omite y se informa.
                    Requiere haber importado el diario (paso 1) y las facturas (paso 5) previamente.
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setMatchError(null);
                        setMatchResult(null);
                        apiManager.execute(contCtrl.matchInvoices(year))
                            .then(data => setMatchResult(data as unknown as EntityResult))
                            .catch(err => setMatchError(err?.response?.data?.message ?? err?.message));
                    }}
                >
                    Vincular facturas con asientos para {year}
                </Button>
                {matchError && <Alert severity="error" sx={{mt: 1}}>{matchError}</Alert>}
                {matchResult && (
                    <Alert severity={matchResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Vinculadas: <strong>{matchResult.updated}</strong> —
                        Omitidas (ambiguas o sin match): <strong>{matchResult.skipped}</strong>
                        {matchResult.errors.length > 0 && (
                            <Box sx={{mt: 0.5}}>
                                {matchResult.errors.map((e, i) => (
                                    <Typography key={i} variant="body2" color="warning.dark">• {e}</Typography>
                                ))}
                            </Box>
                        )}
                    </Alert>
                )}

                </Panel>

                {/* ── Match bills (emitidas) with journal entries ── */}
                <Panel title="8. Vincular facturas emitidas con asientos">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Vincula las facturas emitidas a clientes (generadas en Arteco) con sus asientos CONTASOL
                    del mismo ejercicio, eliminando el asiento borrador AUTO_FACTURA duplicado.
                    Criterio: misma fecha de emisión, cuenta contable del cliente (43x) e importe total
                    con IVA dentro de ±0,05 €. Requiere que el cliente tenga cuenta contable asignada
                    (paso 5) y el diario importado (paso 1).
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setMatchBillsError(null);
                        setMatchBillsResult(null);
                        apiManager.execute(contCtrl.matchBills(year))
                            .then(data => setMatchBillsResult(data as unknown as EntityResult))
                            .catch(err => setMatchBillsError(err?.response?.data?.message ?? err?.message));
                    }}
                >
                    Vincular facturas emitidas con asientos para {year}
                </Button>
                {matchBillsError && <Alert severity="error" sx={{mt: 1}}>{matchBillsError}</Alert>}
                {matchBillsResult && (
                    <Alert severity={matchBillsResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Vinculadas: <strong>{matchBillsResult.updated}</strong> —
                        Omitidas (ambiguas o sin match): <strong>{matchBillsResult.skipped}</strong>
                        {matchBillsResult.errors.length > 0 && (
                            <Box sx={{mt: 0.5}}>
                                {matchBillsResult.errors.map((e, i) => (
                                    <Typography key={i} variant="body2" color="warning.dark">• {e}</Typography>
                                ))}
                            </Box>
                        )}
                    </Alert>
                )}

                </Panel>

                {/* ── Reconcile with Contasol (standalone) ── */}
                <Panel title="9. Reconciliar con Contasol">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Ejecuta la reconciliación completa del ejercicio sin re-importar: vincula facturas
                    (recibidas y emitidas) con sus asientos y descarta los asientos AUTO_FACTURA —en borrador
                    o confirmados— que ya tienen asiento CONTASOL equivalente, renumerando después el
                    ejercicio para que el diario no quede con saltos. Es idempotente — el paso 1 ya la
                    ejecuta tras cada import; este botón sirve para limpiar a mano.
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setReconcileError(null);
                        setReconcileResult(null);
                        apiManager.execute(contCtrl.reconcile1(year))
                            .then(data => setReconcileResult(data as unknown as ReconcileResult))
                            .catch(err => setReconcileError(err?.response?.data?.message ?? err?.message));
                    }}
                >
                    Reconciliar con Contasol para {year}
                </Button>
                {reconcileError && <Alert severity="error" sx={{mt: 1}}>{reconcileError}</Alert>}
                {reconcileResult && (
                    <Alert severity={reconcileResult.errors.length > 0 ? "warning" : "success"} sx={{mt: 1}}>
                        Facturas vinculadas: <strong>{reconcileResult.billsMatched}</strong> —
                        Asientos AUTO_FACTURA descartados: <strong>{reconcileResult.autoFacturaMatched}</strong>
                        {reconcileResult.errors.length > 0 && (
                            <Box sx={{mt: 0.5}}>
                                {reconcileResult.errors.map((e, i) => (
                                    <Typography key={i} variant="body2" color="warning.dark">• {e}</Typography>
                                ))}
                            </Box>
                        )}
                    </Alert>
                )}

                </Panel>

                {/* ── Divergences: what the two books don't agree on ── */}
                <Panel title="10. Divergencias con Contasol">
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    Las dos contabilidades se llevan en paralelo: aquí se puede confirmar sin esperar al
                    import de Contasol. Esta lista es la contrapartida — los asientos AUTO_FACTURA
                    <strong> confirmados</strong> que el diario de Contasol no refleja. Mientras tenga filas,
                    los dos libros no cuentan lo mismo. Si aparece una <em>fecha en Contasol</em>, el mismo
                    importe está allí pero en otro día: la reconciliación exige fecha idéntica, así que ese
                    caso no se arregla solo y el importe se cuenta dos veces hasta que se cuadren las fechas.
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        setDivergenceError(null);
                        setDivergences(null);
                        apiManager.execute(contCtrl.divergences(year))
                            .then(data => setDivergences(data as unknown as DivergenceRow[]))
                            .catch(err => setDivergenceError(err?.response?.data?.message ?? err?.message));
                    }}
                >
                    Ver divergencias de {year}
                </Button>
                {divergenceError && <Alert severity="error" sx={{mt: 1}}>{divergenceError}</Alert>}
                {divergences && (
                    <Box sx={{mt: 2}}>
                        <Alert severity={divergences.length === 0 ? "success" : "warning"} sx={{mb: 2}}>
                            {divergences.length === 0
                                ? "Ninguna: todo lo confirmado en la Extranet está también en Contasol."
                                : `${divergences.length} asiento(s) confirmados que Contasol no refleja`}
                        </Alert>
                        {divergences.length > 0 && (
                            <Table size="small" sx={{tableLayout: "fixed"}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{width: 90}}>Asiento</TableCell>
                                        <TableCell sx={{width: 110}}>Fecha</TableCell>
                                        <TableCell>Descripción</TableCell>
                                        <TableCell align="right" sx={{width: 120}}>Importe</TableCell>
                                        <TableCell sx={{width: 190}}>Fecha en Contasol</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {divergences.map(row => (
                                        <TableRow key={row.entryId} sx={{backgroundColor: "#fff3e0"}}>
                                            <TableCell sx={{fontFamily: "monospace"}}>{row.entryId}</TableCell>
                                            <TableCell>{row.entryDate}</TableCell>
                                            <TableCell>{row.description}</TableCell>
                                            <TableCell align="right" sx={{fontFamily: "monospace"}}>
                                                {fmtAmt(row.amount)}
                                            </TableCell>
                                            <TableCell>
                                                {row.possibleContasolDate
                                                    ? <Chip label={row.possibleContasolDate} color="warning" size="small"/>
                                                    : <Typography variant="body2" color="text.secondary">no está</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                )}
                </Panel>
            </Stack>
        </Page>
    );
};
